import { Controller } from "@hotwired/stimulus"

const DEFAULT_COUNTRY_CODE = "+27"

export default class extends Controller {
  static targets = ["dateOfBirth", "section", "checkbox", "name", "email", "mobile", "mobileCountryInput"]
  static values = {
    threshold: Number,
    userName: String,
    userEmail: String,
    userCountryCode: String,
    userMobileNumber: String
  }

  connect() {
    if (this.hasDateOfBirthTarget) {
      this.dateOfBirthTarget.addEventListener("input", this.updateSection)
      this.dateOfBirthTarget.addEventListener("change", this.updateSection)
    }
    this.updateSection()
  }

  disconnect() {
    if (this.hasDateOfBirthTarget) {
      this.dateOfBirthTarget.removeEventListener("input", this.updateSection)
      this.dateOfBirthTarget.removeEventListener("change", this.updateSection)
    }
  }

  updateSection = () => {
    const underage = this.isUnderage()
    this.toggleSection(underage)
    this.toggleRequirements(underage)
    if (!underage && this.hasCheckboxTarget) {
      this.checkboxTarget.checked = false
    }
  }

  toggleAccountDetails(event) {
    if (!event.target.checked) return
    this.populateFromAccount()
  }

  isUnderage() {
    if (!this.hasDateOfBirthTarget) return false
    const value = this.dateOfBirthTarget.value
    if (!value) return false

    const [year, month, day] = value.split("-").map(part => parseInt(part, 10))
    if (!year || !month || !day) return false

    const birthDate = new Date(year, month - 1, day)
    if (Number.isNaN(birthDate.getTime())) return false

    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age -= 1
    }

    const threshold = this.thresholdValue || 18
    return age < threshold
  }

  toggleSection(show) {
    if (!this.hasSectionTarget) return
    if (show) {
      this.sectionTarget.classList.remove("hidden")
      this.sectionTarget.setAttribute("aria-hidden", "false")
    } else {
      this.sectionTarget.classList.add("hidden")
      this.sectionTarget.setAttribute("aria-hidden", "true")
    }
  }

  toggleRequirements(needed) {
    if (this.hasNameTarget) {
      this.nameTarget.required = needed
      this.toggleAriaRequired(this.nameTarget, needed)
    }
    if (this.hasEmailTarget) {
      this.emailTarget.required = needed
      this.toggleAriaRequired(this.emailTarget, needed)
    }
    if (this.hasMobileTarget) {
      this.mobileTarget.required = needed
      this.toggleAriaRequired(this.mobileTarget, needed)
    }
  }

  toggleAriaRequired(element, needed) {
    if (!element) return
    if (needed) {
      element.setAttribute("aria-required", "true")
    } else {
      element.removeAttribute("aria-required")
    }
  }

  populateFromAccount() {
    const name = this.userNameValue
    if (this.hasNameTarget && name) {
      this.nameTarget.value = name
    }

    const email = this.userEmailValue
    if (this.hasEmailTarget && email) {
      this.emailTarget.value = email
    }

    const countryCode = this.normalizeCountryCode(this.userCountryCodeValue)
    if (this.hasMobileCountryInputTarget) {
      this.setCountryCode(countryCode)
    }

    if (this.hasMobileTarget && this.userMobileNumberValue) {
      const strippedNumber = this.stripCountryCode(this.userMobileNumberValue, countryCode)
      if (strippedNumber) {
        this.setMobileNumber(countryCode, strippedNumber)
      }
    }
  }

  setCountryCode(code) {
    if (!this.hasMobileCountryInputTarget) return
    const normalizedCode = this.normalizeCountryCode(code)
    this.mobileCountryInputTarget.value = normalizedCode

    const selectorElement = this.mobileCountryInputTarget.closest("[data-controller~='country-code-selector']")
    if (selectorElement) {
      const selectorController = this.application.getControllerForElementAndIdentifier(selectorElement, "country-code-selector")
      if (selectorController) {
        selectorController.selectedCode = normalizedCode
        selectorController.inputTarget.value = normalizedCode
        selectorController.updateButtonLabel()
        selectorController.dispatchChange()
        return
      }
    }

    this.dispatchCountryCodeChange(normalizedCode)
  }

  setMobileNumber(code, number) {
    const mobileElement = this.mobileTarget
    if (!mobileElement) return

    const sanitized = `${number}`.trim()
    if (!sanitized) return

    const composite = `${code} ${sanitized}`.trim()
    const container = mobileElement.closest("[data-controller~='mobile-number']")
    const mobileController = container ? this.application.getControllerForElementAndIdentifier(container, "mobile-number") : null

    if (mobileController) {
      mobileController.currentCode = code
      mobileController.digits = mobileController.extractDigits(composite)
      mobileController.applyMask()
    } else {
      mobileElement.value = composite
    }
  }

  dispatchCountryCodeChange(code) {
    if (!this.hasMobileCountryInputTarget) return
    const detail = {
      code,
      inputId: this.mobileCountryInputTarget.id
    }
    window.dispatchEvent(new CustomEvent("country-code:changed", { detail }))
  }

  stripCountryCode(number, countryCode) {
    if (!number) return ""
    const digits = `${number}`.replace(/\D+/g, "")
    const codeDigits = `${countryCode || ""}`.replace(/\D+/g, "")
    if (codeDigits && digits.startsWith(codeDigits)) {
      return digits.slice(codeDigits.length)
    }
    return digits
  }

  normalizeCountryCode(value) {
    const digits = `${value || ""}`.replace(/\D+/g, "")
    if (digits.length === 0) {
      return DEFAULT_COUNTRY_CODE
    }
    return `+${digits}`
  }
}
